import Link from 'next/link';
import { useMemo, useState } from 'react';

import { TokenPriceWidget } from '@/components/TokenPriceWidget';
import { InlineAlertBox } from '@/components/TransactionModule/Alerts/InlineAlertBox';

import {
  isAdvancedOrderTypeSupported,
  isTriggerOrderType,
  type TriggerOrderType,
} from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useUSDPrices } from '@/hooks/useUSDPrices';
import { useWalletOpenLimitOrders } from '@/hooks/useWalletOpenLimitOrders';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { LoadingSpinner } from '@/ui/Icons/Animated/LoadingSpinner';

import type { ChainAddress, EthereumAddress, SolanaAddress } from '@/utils/models';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import { applyOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';
import { routes } from '@/utils/routes';

import { TriggerOrderRow } from './TriggerOrderRow';
import type { PlacedOrder } from './types';
import { usePostSwapTriggerOrder } from './usePostSwapTriggerOrder';
import {
  getPostSwapOutputCandidates,
  getTriggerPercentage,
  getTriggerRateFromPercentage,
  type TriggerOrderUiLocation,
} from './utils';

type TriggerOrdersCommonProps = {
  chainId: number;
  displaySellAmount: string;
  onCancel: (hasPlacedOrder: boolean) => void;
  sellAmount: string;
  sellTokenAddress: ChainAddress;
  uiLocation: TriggerOrderUiLocation;
};

export type TriggerOrdersProps = TriggerOrdersCommonProps &
  (
    | {
        referencePriceUsd: number;
        referenceTimestamp?: never;
      }
    | {
        referencePriceUsd?: never;
        referenceTimestamp: number;
      }
  );

type TriggerDraft = {
  percentage: number;
  price: string;
};

const TRIGGER_ORDER_TYPES = ['take-profit', 'stop-loss'] as const satisfies TriggerOrderType[];

export function TriggerOrders({
  chainId,
  displaySellAmount,
  onCancel,
  referencePriceUsd,
  referenceTimestamp,
  sellAmount,
  sellTokenAddress,
  uiLocation,
}: TriggerOrdersProps) {
  const stablecoinAddresses = getPostSwapOutputCandidates(sellTokenAddress, chainId);
  const primaryOutputAddress = stablecoinAddresses[0];
  const secondaryOutputAddress = stablecoinAddresses[1];
  const tertiaryOutputAddress = stablecoinAddresses[2];
  const isSupported = isAdvancedOrderTypeSupported(chainId, 'stop-loss');
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const hasWallet = chainId === CHAIN_IDS.SOLANA ? Boolean(solanaWallet) : Boolean(ethereumWallet);
  const { token: sellToken } = useTokenResult(sellTokenAddress, chainId);
  const { token: primaryOutputToken } = useTokenResult(primaryOutputAddress, chainId);
  const { token: secondaryOutputToken } = useTokenResult(secondaryOutputAddress, chainId);
  const { token: tertiaryOutputToken } = useTokenResult(tertiaryOutputAddress, chainId);
  const { data: openOrders } = useWalletOpenLimitOrders({
    funders: {
      ethereum:
        isSupported && chainId !== CHAIN_IDS.SOLANA
          ? (ethereumWallet?.address as EthereumAddress | undefined)
          : undefined,
      solana:
        isSupported && chainId === CHAIN_IDS.SOLANA
          ? (solanaWallet?.address as SolanaAddress | undefined)
          : undefined,
    },
    chainId,
    makerToken: sellTokenAddress,
    page: 1,
    limit: 1000,
  });
  const [selectedOutputAddress, setSelectedOutputAddress] = useState(primaryOutputAddress);
  const outputTokens = [primaryOutputToken, secondaryOutputToken, tertiaryOutputToken].filter(
    (token): token is NonNullable<typeof token> => token !== undefined,
  );
  const selectedOutputToken =
    outputTokens.find((token) => token.address === selectedOutputAddress) ?? primaryOutputToken;
  const priceInputs = useMemo(() => {
    if (!isSupported || !sellToken || referencePriceUsd !== undefined) return undefined;
    return [
      {
        address: sellToken.address,
        networkId: sellToken.chainId,
        ...(referenceTimestamp === undefined
          ? { issuer: sellToken.issuer }
          : { timestamp: referenceTimestamp }),
      },
    ];
  }, [isSupported, referencePriceUsd, referenceTimestamp, sellToken]);
  const { data: usdPrices } = useUSDPrices(priceInputs, false);
  const sellTokenUsd =
    referencePriceUsd ??
    usdPrices?.find(
      (price) =>
        price !== null &&
        sellToken !== undefined &&
        price.networkId === sellToken.chainId &&
        normalizeTokenAddress(price.address, price.networkId) ===
          normalizeTokenAddress(sellToken.address, sellToken.chainId),
    )?.priceUsd ??
    0;
  const referenceRate = sellTokenUsd > 0 ? sellTokenUsd.toString() : '';
  const { submitPostSwapTriggerOrder, isPending } = usePostSwapTriggerOrder();
  const [pendingOrderType, setPendingOrderType] = useState<TriggerOrderType>();
  const [drafts, setDrafts] = useState<Record<TriggerOrderType, TriggerDraft | null>>({
    'stop-loss': null,
    'take-profit': null,
  });
  const [placedOrders, setPlacedOrders] = useState<Partial<Record<TriggerOrderType, PlacedOrder>>>(
    {},
  );

  if (
    !isSupported ||
    !hasWallet ||
    !primaryOutputAddress ||
    !sellToken ||
    !primaryOutputToken ||
    !selectedOutputToken
  ) {
    return null;
  }

  const isTriggerRateValid = (orderType: TriggerOrderType) => {
    const draft = drafts[orderType];
    if (draft === null || !Number.isFinite(Number(draft.price)) || Number(draft.price) <= 0) {
      return false;
    }
    if (!referenceRate) return false;
    return orderType === 'take-profit'
      ? Number(draft.price) > Number(referenceRate)
      : Number(draft.price) < Number(referenceRate);
  };
  const pendingOrderTypes = TRIGGER_ORDER_TYPES.filter(
    (orderType) => drafts[orderType] !== null && !placedOrders[orderType],
  );
  const canSave =
    pendingOrderTypes.length > 0 &&
    pendingOrderTypes.every(isTriggerRateValid) &&
    !isPending &&
    pendingOrderType === undefined;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      for (const orderType of pendingOrderTypes) {
        const triggerRate = drafts[orderType]?.price;
        if (triggerRate === undefined) continue;
        setPendingOrderType(orderType);
        const placedOrder = await submitPostSwapTriggerOrder({
          buyToken: selectedOutputToken,
          buyTokenUsd: 1,
          orderType,
          sellAmount,
          sellToken,
          sellTokenUsd,
          triggerRate,
          uiLocation,
        });
        if (!placedOrder) return;
        setPlacedOrders((orders) => ({
          ...orders,
          [orderType]: { ...placedOrder, outputSymbol: selectedOutputToken.symbol },
        }));
      }
    } finally {
      setPendingOrderType(undefined);
    }
  };

  const handleOutputTokenChange = (address: ChainAddress) => {
    setSelectedOutputAddress(address);
    setDrafts({ 'stop-loss': null, 'take-profit': null });
  };

  const handlePriceChange = (orderType: TriggerOrderType, price: string | null) => {
    setDrafts((current) => {
      if (price === null) return { ...current, [orderType]: null };
      const percentage =
        referenceRate && Number.isFinite(Number(price)) && Number(price) > 0
          ? getTriggerPercentage(referenceRate, price)
          : (current[orderType]?.percentage ?? 0);
      return {
        ...current,
        [orderType]: { percentage, price },
      };
    });
  };

  const handlePercentageChange = (orderType: TriggerOrderType, percentage: number) => {
    setDrafts((current) => ({
      ...current,
      [orderType]: {
        percentage,
        price: referenceRate
          ? getTriggerRateFromPercentage(referenceRate, percentage)
          : (current[orderType]?.price ?? ''),
      },
    }));
  };

  const disabled = isPending || pendingOrderType !== undefined;
  const hasPlacedOrder =
    placedOrders['stop-loss'] !== undefined || placedOrders['take-profit'] !== undefined;
  const placedOrderIds = new Set(
    Object.values(placedOrders).flatMap((order) => (order ? [order.orderId] : [])),
  );
  const hasExistingTriggerOrders =
    openOrders !== undefined &&
    applyOpenLimitOrderTombstones(openOrders).records.some(
      (order) =>
        isTriggerOrderType(order.advancedOrderType) &&
        (order.provider !== 'definitive' || !placedOrderIds.has(order.orderId)),
    );

  return (
    <section
      className="flex min-h-0 flex-1 flex-col gap-6 px-10 pt-10"
      aria-label="Take Profit and Stop Loss"
    >
      <div className="flex flex-col gap-4">
        <div>
          <h2 className={fontStyles({ variant: 'H5', class: 'mb-2 text-center' })}>
            Take Profit and Stop Loss
          </h2>
          <p
            className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-800 text-center' })}
          >
            Sell {displaySellAmount} {sellToken.symbol}
            {outputTokens.length === 1 && ` for ${primaryOutputToken.symbol}`} when your target is
            reached.
          </p>
        </div>
        {hasExistingTriggerOrders && (
          <InlineAlertBox
            level="info"
            title={`You already have open TP/SL orders for ${sellToken.symbol}`}
            description={
              <span>
                New orders will be added alongside your existing orders. You can manage open orders{' '}
                <Link
                  className={linkStyles({ underline: true, font: 'LABEL_MD' })}
                  href={{ pathname: routes.TRADE_HISTORY, query: { tab: 'open-orders' } }}
                >
                  here
                </Link>
                .
              </span>
            }
          />
        )}
        <TokenPriceWidget token={sellToken} />
        {outputTokens.length > 1 && (
          <div className="flex items-center gap-2 mt-1">
            <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-800' })}>
              Receive
            </span>
            <div className="flex gap-1" role="group" aria-label="Order output token">
              {outputTokens.map((token) => (
                <button
                  key={token.address}
                  className={buttons({
                    size: 'small',
                    shape: 'rounded',
                    color: selectedOutputAddress === token.address ? 'lightPurple' : 'gray',
                  })}
                  aria-pressed={selectedOutputAddress === token.address}
                  disabled={disabled || Object.keys(placedOrders).length > 0}
                  onClick={() => handleOutputTokenChange(token.address)}
                >
                  {token.symbol}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <TriggerOrderRow
        disabled={disabled}
        isPending={pendingOrderType === 'take-profit'}
        onChangePercentage={(percentage) => handlePercentageChange('take-profit', percentage)}
        onChangePrice={(price) => handlePriceChange('take-profit', price)}
        onClear={() => setDrafts((current) => ({ ...current, 'take-profit': null }))}
        orderType="take-profit"
        outputSymbol={selectedOutputToken.symbol}
        percentage={drafts['take-profit']?.percentage ?? null}
        placedOrder={placedOrders['take-profit']}
        referenceRate={referenceRate}
        sellAmountUsd={Number(sellAmount) * sellTokenUsd}
        triggerRate={drafts['take-profit']?.price ?? null}
      />
      <TriggerOrderRow
        disabled={disabled}
        isPending={pendingOrderType === 'stop-loss'}
        onChangePercentage={(percentage) => handlePercentageChange('stop-loss', percentage)}
        onChangePrice={(price) => handlePriceChange('stop-loss', price)}
        onClear={() => setDrafts((current) => ({ ...current, 'stop-loss': null }))}
        orderType="stop-loss"
        outputSymbol={selectedOutputToken.symbol}
        percentage={drafts['stop-loss']?.percentage ?? null}
        placedOrder={placedOrders['stop-loss']}
        referenceRate={referenceRate}
        sellAmountUsd={Number(sellAmount) * sellTokenUsd}
        triggerRate={drafts['stop-loss']?.price ?? null}
      />
      <div className="mt-auto flex gap-2.5 pb-4 desktop:pb-6">
        <button
          className={buttons({
            size: 'large',
            color: 'gray',
            shape: 'pill',
            class: 'flex-1',
          })}
          disabled={disabled}
          onClick={() => onCancel(hasPlacedOrder)}
          type="button"
        >
          {hasPlacedOrder ? 'Done' : 'Cancel'}
        </button>
        <button
          aria-busy={disabled}
          className={buttons({
            size: 'large',
            color: 'black',
            shape: 'pill',
            class: 'flex-1',
          })}
          disabled={!canSave}
          onClick={() => void handleSave()}
          type="button"
        >
          {disabled ? <LoadingSpinner className="ml-0" variant="white" /> : 'Save'}
        </button>
      </div>
    </section>
  );
}
